import { format, parseISO } from "date-fns";

import { SCHEDULE_ITEM_STATUS } from "components/AppointmentsList/constants/scheduleItemStatus";

export const isBookedConsultation = (meeting) =>
  meeting.type === SCHEDULE_ITEM_STATUS.CONSULTATION;

export const isTimeOffBlock = (meeting) =>
  meeting.type === SCHEDULE_ITEM_STATUS.STANDARD_EXCEPTION ||
  meeting.type === SCHEDULE_ITEM_STATUS.RECURRING_EXCEPTION;

// The canonical service label for a booked meeting — shared by the agenda
// popover, the compact chip, and the month-level service summary/colour
// legend, so all three group and colour a given consultation identically.
export const getServiceName = (meeting) =>
  meeting.consultation?.practiceService?.name || meeting.consultation?.serviceName || "Appointment";

const clampToRange = (date, min, max) => (date < min ? min : date > max ? max : date);

// Standard interval merge — collapses overlapping/adjacent occupied spans so
// free time is computed against actual coverage, not against however many
// (possibly overlapping, if the practice allows overbooking) individual
// bookings happen to exist.
const mergeIntervals = (intervals) => {
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const merged = [];

  for (const interval of sorted) {
    const last = merged[merged.length - 1];

    if (last && interval.start <= last.end) {
      last.end = interval.end > last.end ? interval.end : last.end;
    } else {
      merged.push({ ...interval });
    }
  }

  return merged;
};

const computeFreeSegments = (practiceStart, practiceEnd, occupied) => {
  const coverage = mergeIntervals(
    occupied
      .map((block) => ({
        start: clampToRange(block.start, practiceStart, practiceEnd),
        end: clampToRange(block.end, practiceStart, practiceEnd),
      }))
      .filter((interval) => interval.end > interval.start)
  );

  const free = [];
  let cursor = practiceStart;

  for (const interval of coverage) {
    if (interval.start > cursor) {
      free.push({ kind: "free", start: cursor, end: interval.start });
    }
    if (interval.end > cursor) cursor = interval.end;
  }

  if (cursor < practiceEnd) {
    free.push({ kind: "free", start: cursor, end: practiceEnd });
  }

  return free;
};

/**
 * One doctor's working day reduced to a single chronological timeline:
 * booked consultations, time off (exceptions), and the free gaps between
 * them, all clipped to that day's practice hours. Free time is computed
 * from merged coverage, so it stays correct even where the practice allows
 * overlapping bookings — a double-booked slot is never miscounted as free.
 *
 * Returns [] when the doctor isn't scheduled to work that day at all (no
 * practice hours to build a timeline against) — nothing to summarise.
 */
export const buildDayAgenda = (doctor) => {
  const practiceStart =
    doctor?.practiceHours?.start && parseISO(doctor.practiceHours.start);
  const practiceEnd =
    doctor?.practiceHours?.end && parseISO(doctor.practiceHours.end);

  if (!practiceStart || !practiceEnd || practiceEnd <= practiceStart) return [];

  const occupied = (doctor.schedule ?? [])
    .filter(
      (meeting) =>
        meeting.start &&
        meeting.end &&
        (isBookedConsultation(meeting) || isTimeOffBlock(meeting))
    )
    .map((meeting) => ({
      kind: isBookedConsultation(meeting) ? "booked" : "off",
      start: parseISO(meeting.start),
      end: parseISO(meeting.end),
      meeting,
    }))
    .filter((block) => block.end > block.start);

  const free = computeFreeSegments(practiceStart, practiceEnd, occupied);

  return [...occupied, ...free].sort((a, b) => a.start - b.start);
};

export const formatDuration = (ms) => {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
};

const sumFreeMs = (agenda) =>
  agenda
    .filter((block) => block.kind === "free")
    .reduce((total, block) => total + (block.end - block.start), 0);

export const describeFreeTime = (agenda) => {
  const freeMs = sumFreeMs(agenda);

  return freeMs > 0 ? `${formatDuration(freeMs)} free` : null;
};

// A short label for whatever time off is on the day — "Off all day" when an
// exception is flagged as such, otherwise the first block's range (plus a
// count of any others), so a partial-day exception still reads at a glance.
export const describeTimeOff = (agenda) => {
  const offBlocks = agenda.filter((block) => block.kind === "off");

  if (offBlocks.length === 0) return null;
  if (offBlocks.some((block) => block.meeting.exception?.isAllDay)) return "Off all day";

  const [first, ...rest] = offBlocks;
  const range = `${format(first.start, "HH:mm")}–${format(first.end, "HH:mm")}`;

  return rest.length > 0 ? `${range} +${rest.length}` : range;
};
